import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, WifiOff, X } from "lucide-react";
import { useMemo, useState } from "react";
import { fetchCatalog, filterByTag, searchListings } from "@/api/store.api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FeaturedCarousel } from "./featured-carousel";
import { STORE_TAGS, tagLabel } from "./labels";
import { ListingCard } from "./listing-card";
import { StoreShell } from "./store-shell";

/** Catalog query plus search and tag filters, shared with the app view. */
export function useCatalogView() {
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState<string | "all">("all");
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: fetchCatalog });
  const listings = catalog.data?.listings ?? [];

  const searching = query.trim().length > 0;
  const featured = useMemo(() => listings.filter(listing => listing.featured), [listings]);
  const visible = useMemo(
    () => (searching ? searchListings(query, listings) : filterByTag(tag, listings)),
    [searching, query, tag, listings],
  );
  // Chips for the known tags plus any other tag a listing carries.
  const tags = useMemo(() => {
    const extra = new Set<string>();
    for (const listing of listings) for (const item of listing.tags) if (!(STORE_TAGS as readonly string[]).includes(item)) extra.add(item);
    return [...STORE_TAGS, ...[...extra].sort()];
  }, [listings]);

  const heading = searching ? "Results" : tag === "all" ? "All miniapps" : tagLabel(tag);

  return { query, setQuery, tag, setTag, catalog, listings, searching, featured, visible, tags, heading };
}

export function CatalogPage() {
  const { query, setQuery, tag, setTag, catalog, listings, searching, featured, visible, tags, heading } = useCatalogView();

  return (
    <StoreShell>
      <div className="space-y-8">
        <div>
          <h1 className="font-display text-3xl font-bold leading-9 sm:text-4xl">Store</h1>
          <p className="mt-1 text-sm text-[#747780] sm:text-base">Miniapps for your glasses</p>
        </div>

        <label className="flex h-12 items-center gap-2 rounded-2xl border border-[#e4e6e2] bg-white px-4 shadow-[0_1px_2px_rgba(20,21,27,0.05)] focus-within:border-[#9fb8aa] focus-within:ring-[3px] focus-within:ring-ring/40">
          <Search className="size-5 shrink-0 text-[#8a8d95]" />
          <input
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search miniapps"
            autoCapitalize="none"
            autoCorrect="off"
            className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-[#8a8d95] [&::-webkit-search-cancel-button]:hidden"
          />
          {searching ? (
            <button type="button" aria-label="Clear search" onClick={() => setQuery("")} className="rounded-full p-1 text-[#8a8d95] hover:bg-[#f3f4f2] hover:text-[#14151b]">
              <X className="size-4" />
            </button>
          ) : null}
        </label>

        {catalog.isError ? (
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#f0d9d5] bg-[#fff6f4] px-4 py-3 text-sm text-[#a64235]">
            <WifiOff className="size-4 shrink-0" />
            <span className="flex-1">Couldn't reach the store.</span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => void catalog.refetch()}>
              Try again
            </Button>
          </div>
        ) : null}

        {!searching ? <FeaturedCarousel listings={featured} /> : null}

        {!searching && listings.length > 0 ? (
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden">
            {(["all", ...tags] as const).map(item => (
              <button
                key={item}
                type="button"
                aria-pressed={tag === item}
                onClick={() => setTag(item)}
                className={cn(
                  "shrink-0 rounded-full border px-3.5 py-1.5 text-[13px] font-medium capitalize transition-colors",
                  tag === item
                    ? "border-[#111217] bg-[#111217] text-white"
                    : "border-[#e4e6e2] bg-white text-[#5d6068] hover:border-[#c9cdc6] hover:text-[#14151b]",
                )}
              >
                {tagLabel(item)}
              </button>
            ))}
          </div>
        ) : null}

        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold">{heading}</h2>
          {catalog.isPending ? (
            <div className="flex items-center gap-2 py-10 text-sm text-[#747780]">
              <Loader2 className="size-4 animate-spin" />
              Loading the catalog…
            </div>
          ) : visible.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#d9dcd7] px-4 py-10 text-center">
              <p className="font-medium">{searching ? `No miniapps match "${query.trim()}"` : "Nothing published yet"}</p>
              {!searching ? <p className="mt-1 text-sm text-[#747780]">Miniapps appear here as developers publish them.</p> : null}
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {visible.map(listing => (
                <ListingCard key={listing.packageName} listing={listing} />
              ))}
            </div>
          )}
        </section>
      </div>
    </StoreShell>
  );
}
